import { useMemo } from "react"
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useMeetingsStore } from "../../store/meetings.store"

export default function TotalMeetingsCard() {
  const t = useTranslation()
  const meetings = useMeetingsStore((s) => s.meetings)
  const hosted   = useMemo(() => meetings.filter(m => m.type === 'instant' || m.type === 'scheduled').length, [meetings])
  const attended = useMemo(() => meetings.filter(m => m.type === 'joined').length, [meetings])
  const total = hosted + attended
  const donutData = [
    { name: "Hosted", value: hosted, color: "var(--primary)" },
    // --primary-400 does not exist, so this slice had no colour and drew black:
    // invisible on a dark card. Matches the legend's bg-primary/60.
    { name: "Attended", value: attended, color: "color-mix(in oklab, var(--primary) 60%, transparent)" },
  ]

  return (
    <div className="flex-1 bg-card rounded-[18px] shadow-sm overflow-hidden">
      {/* Header with border bottom */}
      <div className="px-4 py-3 border-b border-stroke-100 dark:border-border">
        <h3 className="text-sm font-semibold text-foreground">{t("meetings.totalMeetings")}</h3>
      </div>

      {/* Content centered and balanced */}
      <div className="p-4 flex items-center justify-center">
        <div className="w-full max-w-[360px] flex items-center md:flex-col lg:flex-row justify-center gap-4">
          <div className="relative w-[130px] h-[130px] shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={32}
                  outerRadius={48}
                  paddingAngle={2}
                  stroke="none"
                >
                  {donutData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "var(--card)", borderColor: "var(--border)" }}
                  itemStyle={{ color: "inherit" }}
                  wrapperClassName="text-primary"
                  formatter={(value: number, name: string) => [value, name]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-foreground">
              {total}
            </div>
          </div>

          <div className="flex flex-col min-w-[140px]">
            <p className="text-2xl font-extrabold text-foreground leading-tight">
              {total}
            </p>

            <p className="text-xs text-muted-foreground mt-1">
              {t("meetings.vsLastMonth")} 2
            </p>

            <div className="flex flex-col gap-1 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary" />
                <span className="text-xs text-muted-foreground">{t("meetings.hosted")}: {hosted}</span>
              </div>

              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-primary/60" />
                <span className="text-xs text-muted-foreground">{t("meetings.attended")}: {attended}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
